const WORD_CHARACTER = /[\p{L}\p{N}]/u

export interface SentenceToken {
  /** Char offset in the sentence text, end-exclusive. */
  end: number
  /** Char offset in the sentence text, end-exclusive. */
  start: number
  value: string
}

/**
 * Splits a sentence on whitespace and peels every leading/trailing run of
 * non-letter characters into its own token (`»Zu` → `»` + `Zu`,
 * `Bahndamm,` → `Bahndamm` + `,`). Internal punctuation stays inside the token
 * (`Petersburg--Warschauer`), so a blanked word never swallows a quote.
 *
 * The token also carries its char offsets: the grammar DBs describe a blank as a
 * char range (`hidden.start` / `hidden.end`), which may cover two tokens or only
 * the ending of one.
 */
export function splitSentenceWithOffsets(text: string): SentenceToken[] {
  return [...text.matchAll(/\S+/g)].flatMap(chunk=> {
    const chunkStart = chunk.index ?? 0
    return splitEdgePunctuationWithOffsets(chunk[0] ?? "", chunkStart)
  })
}

/** Letters and digits only, lowercased — the key two tokens are compared by. */
export function normalizeKey(token: string): string {
  return token.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "")
}

export function splitSentence(text: string): string[] {
  return splitSentenceWithOffsets(text).map(token=> token.value)
}

function splitEdgePunctuationWithOffsets(
  chunk: string,
  chunkStart: number,
): SentenceToken[] {
  // Split by UTF-16 code unit, not code point: the offsets must line up with
  // `String.prototype.slice` on the same text, which is what the DB ranges use.
  const characters = chunk.split("")
  const start = characters.findIndex(isWordCharacter)
  if (start === -1) return [createToken(chunk, chunkStart)]

  const end = characters.findLastIndex(isWordCharacter) + 1
  return [
    createToken(characters.slice(0, start).join(""), chunkStart),
    createToken(characters.slice(start, end).join(""), chunkStart + start),
    createToken(
      characters.slice(end).join(""),
      chunkStart + end,
    ),
  ].filter(token=> token.value.length > 0)
}

function createToken(value: string, start: number): SentenceToken {
  return { end: start + value.length, start, value }
}

function isWordCharacter(character: string): boolean {
  return WORD_CHARACTER.test(character)
}
